'use client';
import React from 'react';
import Image from 'next/image';
import { cn } from '@/app/lib/utils';
import Icon, { IconName } from './Icon';
import { LucideIcon } from 'lucide-react';
import { House, Book, Radio, Code } from 'lucide-react';

import InternalOnlyTooltip from './InternalOnlyTooltip';

export type SidebarMenuItemProps = {
  icon?: IconName;
  lucideIcon?: LucideIcon;
  src?: string;
  alt?: string;
  label: string;
  subtext?: string;
  action?: React.ReactNode;
  className?: string;
  active?: boolean;
  isCircle?: boolean;
  iconWrapperClassName?: string;
  wrapperClassName?: string;
  isInternal?: boolean;
};

/** Returns the extra hover-animation class for known special icons. */
function getIconAnimationClass(LucideIconComponent?: LucideIcon): string {
  if (LucideIconComponent === House || LucideIconComponent === Book) {
    // Home & Knowledge Base: hop
    return 'group-hover:animate-sidebar-hop';
  }
  if (LucideIconComponent === Radio) {
    // Signals: pulse
    return 'group-hover:animate-sidebar-pulse';
  }
  if (LucideIconComponent === Code) {
    // Developers: stretch
    return 'group-hover:animate-sidebar-stretch';
  }
  return '';
}

export default function SidebarMenuItem({
  icon,
  lucideIcon: LucideIconComponent,
  src,
  alt,
  label,
  subtext,
  action,
  className,
  active = false,
  isCircle = false,
  iconWrapperClassName,
  wrapperClassName,
  isInternal = false,
}: SidebarMenuItemProps) {
  const iconAnimClass = getIconAnimationClass(LucideIconComponent);

  return (
    <div className={cn('relative transition-all px-5', wrapperClassName)}>
      <div
        data-active={active || undefined}
        className={cn(
          'sidebar-nav-item flex items-center rounded-[10px] px-2 py-1.5 group cursor-pointer transition-all duration-300',
          active
            ? 'bg-primary/10 text-primary'
            : 'bg-background text-secondary-foreground hover:bg-primary/5 hover:text-foreground active:scale-[0.98] active:translate-y-px',
          className
        )}
      >
        <div
          className={cn(
            'relative flex items-center justify-center shrink-0',
            isCircle ? 'w-11 h-11' : 'w-5 h-5',
            iconWrapperClassName
          )}
        >
          {LucideIconComponent ? (
            <LucideIconComponent
              size={16}
              aria-label={label}
              className={cn(
                'transition-transform duration-200',
                active && 'scale-[1.05]',
                !active && 'group-hover:scale-[1.05]',
                !active && iconAnimClass
              )}
            />
          ) : icon ? (
            <Icon
              name={icon}
              size={4}
              aria-label={label}
              className={cn(
                'transition-transform duration-200',
                active && 'scale-[1.05]',
                !active && 'group-hover:scale-[1.05]'
              )}
            />
          ) : (
            src && (
              <div className="w-full h-full relative">
                <Image
                  src={src}
                  alt={alt || ''}
                  width={40}
                  height={40}
                  className="w-full h-full rounded-full object-contain"
                />
              </div>
            )
          )}
        </div>
        <div className="flex items-center justify-between w-full gap-1">
          <div className={cn('flex flex-col transition-all overflow-hidden w-auto pl-2')}>
            <div className="flex items-center gap-1">
              <span
                className={cn(
                  'text-[14px]',
                  active && 'font-medium',
                  !active && 'group-hover:font-medium'
                )}
              >
                {label}
              </span>
              {isInternal && <InternalOnlyTooltip className="px-1.5 py-0.5" side="right" />}
            </div>
            {subtext && <span className="text-xs">{subtext}</span>}
          </div>
        </div>
        {action && action}
      </div>
    </div>
  );
}
